import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { offices } from '../../data/mockData';

const GlobalPresenceSection = () => {
  const { currentLang, t, isRTL } = useLanguage();
  const officeList = offices[currentLang] || offices.fr;

  return (
    <section className="py-24 bg-white relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background Map - Africa */}
      <div className="absolute inset-0 opacity-10">
        <svg
          viewBox="0 0 1200 600"
          className="w-full h-full"
          preserveAspectRatio="xMidYMid slice"
        >
          {Array.from({ length: 40 }).map((_, row) =>
            Array.from({ length: 80 }).map((_, col) => {
              const x = col * 15 + 7;
              const y = row * 15 + 7;
              const isAfrica = (x > 500 && x < 750 && y > 80 && y < 450);
              const opacity = isAfrica ? 0.8 : 0;
              return (
                <circle
                  key={`${row}-${col}`}
                  cx={x}
                  cy={y}
                  r={2.5}
                  fill="#9B4B8C"
                  opacity={opacity}
                />
              );
            })
          )}
        </svg>
      </div>

      {/* Abidjan Marker */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
          className="absolute top-[45%] left-[52%]"
        >
          <div className="relative">
            <div className="w-4 h-4 bg-[#9B4B8C] rounded-full animate-ping absolute" />
            <div className="w-4 h-4 bg-[#9B4B8C] rounded-full relative" />
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
          >
            {t.presence.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            {t.presence.subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link
              to="/presence"
              className={`inline-flex items-center gap-2 bg-[#9B4B8C] text-white px-8 py-4 rounded-lg font-medium hover:bg-[#7d3c71] transition-colors shadow-lg hover:shadow-xl ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <MapPin className="w-5 h-5" />
              {t.presence.buttonText}
            </Link>
          </motion.div>
        </div>

        {/* Office Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20 max-w-md mx-auto"
        >
          {officeList.map((office) => (
            <div
              key={office.id}
              className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow text-center"
            >
              <div className="w-12 h-12 bg-[#9B4B8C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-[#9B4B8C]" />
              </div>
              <h3 className="font-semibold text-gray-900 text-lg">{office.region}</h3>
              <p className="text-[#9B4B8C] font-medium mb-2">{office.city}</p>
              <p className="text-gray-600 text-sm">{office.address}</p>
              <p className="text-gray-600 text-sm">{office.phone}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default GlobalPresenceSection;
